'use client';

import { useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';
import { CatHeading } from '@blocks-react/components/Heading';
import { CatIconClose } from '@blocks-react/components/Icons/Close';
import style from './ContextualMenu.module.scss';

type ContextualMenuProps = Readonly<{
  title: string;
  titleVariant: 'title';
  showContextualMenu: boolean;
  setShowContextualMenu: (value: boolean) => void;
  children: React.ReactNode;
}>;

export const ContextualMenu = ({
  title,
  titleVariant,
  showContextualMenu,
  setShowContextualMenu,
  children
}: ContextualMenuProps) => {
  const handleCloseContextualMenu = useCallback(() => {
    setShowContextualMenu(false);
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        event.preventDefault();
        setShowContextualMenu(false);
        document.body.style.overflow = 'auto';
      }
    };
    if (showContextualMenu) {
      document.addEventListener('mouseup', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [showContextualMenu]);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCloseContextualMenu();
    }
  };

  return (
    <div
      className={style['contextualmenu-overlay']}
      data-testid={'equipment-actions-menu'}
      onClick={(event) => handleClick(event)}
      role={'menuitem'}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className={cx(style['contextualmenu-wrapper'])} ref={ref}>
        <div
          className={cx(
            style['contextualmenu-wrapper__heading'],
            'd-flex justify-content-between'
          )}
        >
          <CatHeading variant={titleVariant}>{title}</CatHeading>
          <CatIconClose
            data-testid="menu-close-icon"
            className={style['contextualmenu-wrapper__close-icon']}
            onClick={handleCloseContextualMenu}
          />
        </div>
        <div
          className={cx(style['contextualmenu-wrapper__menu-actions'], 'p-4')}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
